import React, { useContext, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import BaseDivideLine from "../../../../components/BaseDivideLine";
import UserInfo from "../UserInfo";
import BaseButton from "../../../../components/BaseButton";
import PropTypes from "prop-types";
import models from "../../../../types/models";
import { useTranslation } from "react-i18next";
import PersonalAccount from "../../../../store/PersonalAccount";
import Game from "../../../../store/Game";
import { isGameAccessExpired } from "../../../../utils/access-check";
import BaseIcon from "../../../../components/BaseIcon";
import ResetModal from "../../../../modals/ResetModal";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useCurrentGame from "../../../../hooks/useCurrentGame";
import useModalActions from "../../../../hooks/useModalActions";
import useCreateBlankForm from "../../../../hooks/useCreateBlankForm";
import RemoveGame from "../RemoveGame";
import { MODAL_TYPES } from "../../../../utils/emitter";
import config from "../../../../config";
import "./style.scss";
import useCurrentUserData from "../../../../hooks/useCurrentUserData";
import BaseTextButton from "../../../../components/BaseTextButton";
import { COUNT_CARDS } from "../../../../utils/constants";
import ROUTES from "../../../../utils/routes";
import {
  needsExtended57,
  userFinishedExtended,
  partnerFinishedExtended,
} from "../../../../utils/extendedProgress";

export default observer(function PartnerGame({ game, startGame }) {
  PartnerGame.propTypes = {
    game: models.game,
    startGame: PropTypes.func.isRequired,
  };
  const { setGame } = useCurrentGame();
  const { getUserData } = useCurrentUserData();
  const { openModal } = useModalActions();
  const { startBlankForm } = useCreateBlankForm();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const partnerGenderByUser =
    PersonalAccount.user.gender === "male" ? "female" : "male";
  const isAccessNotAvailable = isGameAccessExpired(game);
  const [matches, setMatches] = useState();
  const matchesRes = Game.countPartnerGameMatches(game.id);

  useEffect(() => {
    setMatches(matchesRes);
  }, [matchesRes]);

  const gameNotificationCount = useMemo(() => {
    const user = getUserData();
    const countEndGame = Game.getEndGameNotificationCount(game.id, user.id);
    return countEndGame;
  }, [game, getUserData]);

  const insightEvent = useMemo(() => {
    const user = getUserData();
    if (game && user) {
      return Game.getInsightEvent(game.id, user.id);
    } else {
      return null;
    }
  }, [game, getUserData]);

  // item 20a: набор +57 куплен, база пройдена, но свои 57 карт игрок ещё не отвечал.
  // Тогда на «Играть» — зелёный кружок, а по нажатию — подтверждение «Продолжить?»
  // (каждый раз, пока не пройдёт). Иначе кнопка работает как обычно.
  const needsExt57 = needsExtended57(game, getUserData()?.id);
  // item 6/7: доступ к набору +57 (полная игра PREMIUM его включает) и статус его
  // прохождения. Пока набор доступен, но НЕ прошли оба — показываем ярлык-статус
  // «+57 карточек» у «Играть» (см. productRows), чтобы человек видел, что тест ждёт.
  const currentUserId = getUserData()?.id;
  const hasExt57Access = game.hasExtendedSet || game.type === "PREMIUM";
  const selfDoneExt = userFinishedExtended(game, currentUserId);
  const partnerDoneExt = partnerFinishedExtended(game, currentUserId);
  // Прямо на +57 (строка «+57 карточек»): инструкция-страница → карты +57.
  // 10.10: игру делаем активной — раньше этот путь её не выбирал, и бургер-меню
  // («Игра», «Анкета») оставалось на другой игре.
  const goExtended57 = () => {
    Game.setGame(game);
    setGame(game);
    navigate(ROUTES.EXTENDED_57_INSTRUCTION_PAGE, {
      state: { gameId: game.id },
    });
  };
  const handlePlay = () => {
    if (needsExt57) {
      // 10.10: 88 пройдены, свои +57 нет. С 13.08 кнопка всегда уводила на +57, и
      // совпадения по 88 было не посмотреть. Возвращаем выбор: «Пройти +57» или
      // «Смотреть совпадения» (обычный путь startGame: правила при первом заходе,
      // ожидание партнёра, если он ещё не прошёл 88).
      openModal(MODAL_TYPES.EXT57_CONTINUE, {
        gameId: game.id,
        onResults: () => startGame(game),
      });
      return;
    }
    startGame(game);
  };

  const editGame = () => {
    Game.setGame(game);
    setGame(game);
    navigate(ROUTES.IN_GAME_EDIT);
  };

  const isGameCanEdit = useMemo(() => {
    // базовый тест (88) завершён → редактирование доступно. Строгое === ломалось
    // после докупки +57: answers становилось 145 и «Редактировать ответы» исчезал.
    return (game?.answers?.length ?? 0) >= COUNT_CARDS;
  }, [PersonalAccount.games]);

  const isOneGameAndOtherInvites = useMemo(() => {
    const gamesWithAcceptedInvitesCount = PersonalAccount.games.filter(
      (game) => game.invitedUserId !== null
    ).length;
    const gamesWithNoAcceptedInvitesCount = PersonalAccount.games.filter(
      (game) => game.invitedUserId === null
    ).length;
    return (
      gamesWithAcceptedInvitesCount === 1 && gamesWithNoAcceptedInvitesCount > 0
    );
  }, [PersonalAccount.games]);

  // Продукты ПОД КАЖДОЙ игрой (дизайн Figma): игра / +57 / пустая анкета —
  // привязаны к конкретной game.id (раньше был один общий блок внизу ЛК).
  const entitlements = PersonalAccount.user?.entitlements;
  const openBuyForGame = (productType, variant) =>
    openModal(MODAL_TYPES.BUY_PRODUCT, {
      productType,
      variant,
      gameId: game.id,
      from: ROUTES.PERSONAL_ACCOUNT_PAGE,
    });
  const productRows = [
    // 03.08: тест из 88 карточек (увидеть результаты, без анкет) — $9. Иконка —
    // пазл-КОНТУР, чтобы отличать от +57 (пазл-заливка). Показываем, пока игра не
    // апгрейднута до полной.
    {
      key: "test",
      icon: "pts_puzzle_outline",
      show: game.type !== "PREMIUM",
      // Task 3: попап «Хочешь увидеть весь тест полностью?» — 2 тарифа (тест-88 +
      // полная игра) + «Обрати внимание!» (Figma 12414-50766) = существующий SeeFullTestModal
      onClick: () =>
        openModal(MODAL_TYPES.SEE_FULL_TEST, {
          gameId: game.id,
          from: ROUTES.PERSONAL_ACCOUNT_PAGE,
        }),
    },
    // item 7: без доступа — предложение КУПИТЬ +57. item 6: с доступом (PREMIUM
    // включает +57 / набор куплен) — ярлык-СТАТУС «+57 карточек» (пазл-заливка),
    // пока набор не прошли ОБА. Клик: свои +57 не пройдены → продолжить
    // (EXT57_CONTINUE); прошёл сам, ждём партнёра → поп-ап «ждём партнёра».
    // Прошли оба — строка скрыта. Так после покупки полной игры +57 не
    // предлагается купить повторно, но тест виден, пока не завершён.
    {
      key: "extended_57",
      icon: "pts_puzzle",
      show: hasExt57Access ? !(selfDoneExt && partnerDoneExt) : true,
      onClick: hasExt57Access
        ? () =>
            selfDoneExt
              ? openModal(MODAL_TYPES.EXT57_WAIT_PARTNER, { gameId: game.id })
              : // свой +57 не пройден: база пройдена → сразу на +57 (это и
                // просили, нажав «+57 карточек»); иначе — обычная игра
                // (нельзя проваливаться в +57 с недоигранной базой)
                needsExt57
                ? goExtended57()
                : handlePlay()
        : () => openBuyForGame("EXTENDED_57"),
    },
    // item 20a: после покупки +57 значок набора убираем (как исчезает «Игра
    // Wepurplay» после апгрейда). Маршрут к 57 картам теперь — зелёный кружок на
    // «Играть» + подтверждение «Продолжить?» (см. needsExt57/handlePlay).
    {
      key: "game",
      icon: "butterfly_gradient",
      show: game.type !== "PREMIUM",
      // Task 3: попап «Игра Wepurplay» — описание игры + 49$/4900
      onClick: () => openBuyForGame("GAME", "game_full"),
    },
    {
      key: "blank_form",
      icon: "pts_anketa",
      show: true,
      onClick: () => {
        // анкета живёт в контексте конкретной игры: синхронизируем «текущую»
        // игру (localStorage) — иначе у юзера с несколькими играми форма и
        // раздел анкет смотрят на другую игру (созданная анкета «не появлялась»,
        // «Отправить» был неактивен без партнёра у текущей демо-игры)
        Game.setGame(game);
        setGame(game);
        // item 4: без попапа названия — сразу анкета (тему вводят внутри формы)
        startBlankForm(game.id, entitlements, ROUTES.PERSONAL_ACCOUNT_PAGE);
      },
    },
  ];

  return (
    <div key={game.id} id={`game-${game.id}`} className="person-pair">
      <ResetModal />
      <BaseDivideLine dash={true} top={true} />
      <div className="person-pair__info">
        <UserInfo
          gameId={game?.id}
          isDemo={game?.demo}
          name={game?.partner.firstName}
          email={game?.partner.email}
          icon={game?.partner.gender}
          hideMatches={false}
          age={game?.partner.age}
        />
        {/* «Редактировать ответы теста» + «Удалить» — в строке имени справа (Figma).
           На моб (фигма 10394-41743) весь блок уезжает второй строкой под имя:
           слева «Редактировать…», справа «Удалить» (14px серым). */}
        {!isAccessNotAvailable && !config.hideRemoveGame && !game?.demo ? (
          <div className="person-pair__name-actions">
            {isGameCanEdit && (
              <BaseTextButton
                onClick={editGame}
                className="person-pair__edit-game-button"
                iconStyle="person-pair__edit-game-ic"
                iconPlace="right"
                icon="pencil"
              >
                {t("personal_acc.edit_game_test")}
              </BaseTextButton>
            )}
            <RemoveGame
              game={game}
              onClick={() => openModal(MODAL_TYPES.REMOVE_GAME, game.id)}
            />
          </div>
        ) : null}
      </div>
      {game.answers.length === 0 &&
      PersonalAccount.user.id === game.invitedUserId ? (
        <div className="person-pair__invite-desc">
          {game.type === "DEMO"
            ? t("personal_acc.invite_to_game_test")
            : t("personal_acc.invite_to_game")}
        </div>
      ) : null}
      {isGameCanEdit && (
        <BaseTextButton
          onClick={editGame}
          className="person-pair__edit-game-button--mobile"
          iconStyle="person-pair__edit-game-ic"
          iconPlace="right"
          icon="pencil"
        >
          {t("personal_acc.edit_game_test")}
        </BaseTextButton>
      )}
      {isAccessNotAvailable ? (
        <div className="person-pair__access-game">
          <div>{t("personal_acc.access_year_finished")}</div>
          <div className="person-pair__access-game-remove">
            <RemoveGame
              game={game}
              onClick={() => openModal(MODAL_TYPES.REMOVE_GAME, game.id)}
            />
          </div>
        </div>
      ) : (
        // одна строка (веб): кнопка · продукты · счётчик совпадений справа (Figma)
        <div className="person-pair__play">
          {matches && matches.coincidences ? (
            <BaseButton
              loading={game.isLoading}
              onClick={handlePlay}
              className="person-pair__play__btn"
              unfilled={Game.game?.id !== game.id}
            >
              {game.type === "DEMO"
                ? t("personal_acc.watch")
                : t("personal_acc.play")}

              {game.type === "PREMIUM" &&
              insightEvent &&
              insightEvent.count > 0 ? (
                <div
                  key={insightEvent.id}
                  className="person-pair__play--item__count"
                >
                  {insightEvent.count + gameNotificationCount}
                </div>
              ) : null}
              {game.type === "PREMIUM" &&
              !insightEvent &&
              gameNotificationCount > 0 ? (
                <div className="person-pair__play--item__count">
                  {gameNotificationCount}
                </div>
              ) : null}
              {game.type !== "PREMIUM" && gameNotificationCount > 0 ? (
                <div className="person-pair__play--item__count">
                  {gameNotificationCount}
                </div>
              ) : null}
              {/* item 20a: зелёный кружок «+57 доступны» (пока игрок не прошёл свои 57) */}
              {needsExt57 ? (
                <div className="person-pair__play--item__count person-pair__play--item__dot" />
              ) : null}
            </BaseButton>
          ) : (
            <BaseButton
              loading={game.isLoading}
              onClick={handlePlay}
              unfilled={Game.game?.id !== game.id}
              className="person-pair__play__btn"
            >
              {t(
                // item 7: >= (не строгое ===): у PREMIUM после докупки +57 answers
                // становится 145, из-за чего «Активировано» пропадало и кнопка
                // возвращалась к «Играть/Начать тест» (как и в isGameCanEdit-фиксе).
                game.type === "DEMO"
                  ? game.answers.length >= +config.totalCards
                    ? "personal_acc.activated"
                    : "personal_acc.start_test"
                  : game.answers.length >= +config.totalCards
                  ? "personal_acc.activated"
                  : "personal_acc.play"
              )}
              {game.type === "PREMIUM" &&
              insightEvent &&
              insightEvent.count > 0 ? (
                <div
                  key={insightEvent.id}
                  className="person-pair__play--item__count"
                >
                  {insightEvent.count + gameNotificationCount}
                </div>
              ) : null}
              {game.type === "PREMIUM" &&
              !insightEvent &&
              gameNotificationCount > 0 ? (
                <div className="person-pair__play--item__count">
                  {gameNotificationCount}
                </div>
              ) : null}
              {game.type !== "PREMIUM" && gameNotificationCount > 0 ? (
                <div className="person-pair__play--item__count">
                  {gameNotificationCount}
                </div>
              ) : null}
              {/* item 20a: зелёный кружок «+57 доступны» (пока игрок не прошёл свои 57) */}
              {needsExt57 ? (
                <div className="person-pair__play--item__count person-pair__play--item__dot" />
              ) : null}
            </BaseButton>
          )}

          {entitlements ? (
            <div className="person-pair__products">
              {productRows
                .filter((row) => row.show)
                .map((row) => (
                  <div
                    key={row.key}
                    className="person-pair__product"
                    onClick={row.onClick}
                  >
                    <BaseIcon
                      name={row.icon}
                      className="person-pair__product-icon"
                    />
                    <span className="person-pair__product-label">
                      {t(`loyalty.game_products.${row.key}`)}
                    </span>
                  </div>
                ))}
            </div>
          ) : null}

          {matches && matches.coincidences ? (
            <div className="tooltip-container">
              <Tooltip
                enterTouchDelay={0}
                title={t("matchesTip")}
                arrow
                placement="top"
              >
                <div className="person-pair__matches">
                  <div className="person-pair__matches__count">
                    {matches.coincidences?.negative?.length +
                      matches.coincidences?.positive?.length +
                      matches.coincidences?.neutral?.length}
                  </div>
                  <BaseIcon name="mozaika" />
                </div>
              </Tooltip>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
});
